/**
 * PrototypeFrame — THE single render primitive for Prototype HTML.
 *
 * Agent/user-authored HTML+JS only ever executes here, inside an
 * opaque-origin sandboxed iframe pointed at a backend frame endpoint
 * (`/api/prototypes/:id/frame`), which serves the bytes with a
 * `Content-Security-Policy: sandbox allow-scripts` response header. That
 * server-side sandbox is the primary boundary: the document gets an opaque
 * origin (no cookies, storage, or parent DOM) at the HTTP layer. The
 * `sandbox="allow-scripts"` attribute here is belt-and-braces — keep it
 * EXACTLY `allow-scripts`, never `allow-same-origin`.
 *
 * The endpoint owns a trusted outer frame with a nested, sandboxed srcdoc.
 * Its frame-src policy blocks navigation by the untrusted document. Do not
 * put the draft directly in this app's srcDoc: it would inherit the app CSP.
 *
 * Spec: docs/superpowers/specs/2026-08-08-prototypes-v1-design.md § Frontend.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PROTOTYPE_VIEW_MESSAGE, type PrototypePreviewView } from "@yagni/shared";

export interface PrototypePoint {
  x: number; y: number; viewportWidth: number; viewportHeight: number;
  scrollX: number; scrollY: number; targetHint: string;
}

export interface PrototypeFrameProps {
  commentMode?: boolean;
  onPoint?: (point: PrototypePoint) => void;
  /** The frame endpoint URL, e.g. `/api/prototypes/<id>/frame?rev=2`. */
  src: string;
  /** Accessible iframe title, e.g. the prototype's title. */
  title: string;
  /**
   * card — a scaled-to-fit live preview, pointer-events off.
   * full — fills its container, interactive.
   */
  mode: "card" | "full";
  /**
   * Design-viewport width in px for the full mode's viewport toggles
   * (e.g. 390 mobile / 768 tablet). Omit for fluid width.
   */
  width?: number;
  /**
   * The preview's view to show (Today / Proposed, a state, a choice). Posted
   * to the frame's bridge on load and whenever it changes; the bridge shows
   * the matching `data-yagni-*` sections and hides the rest. Omit to let the
   * bridge show its defaults.
   */
  view?: PrototypePreviewView;
}

/**
 * The app-origin frame endpoint for a prototype revision. The iframe request
 * is same-origin, so the browser sends the session cookie and the backend
 * applies the same auth + desk-audience visibility as the JSON detail GET.
 * Pinning `rev` also makes the URL change when the revision does, which is
 * what reloads the iframe.
 */
export function prototypeFrameUrl(prototypeId: string, rev?: number): string {
  const base = `/api/prototypes/${encodeURIComponent(prototypeId)}/frame`;
  return rev !== undefined ? `${base}?rev=${rev}` : base;
}

/** The design viewport a card preview renders at before scaling down. */
const CARD_DESIGN_WIDTH = 1024;
/** Preview window height as a fraction of its width (a 16:10 stage). */
const CARD_ASPECT = 0.625;

export function PrototypeFrame({ src, title, mode, width, commentMode = false, onPoint, view }: PrototypeFrameProps) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const cardFrameRef = useRef<HTMLIFrameElement | null>(null);
  const viewKey = view ? JSON.stringify(view) : null;
  const sendView = () => {
    if (!view) return;
    const target = (mode === "card" ? cardFrameRef : frameRef).current?.contentWindow;
    target?.postMessage({ type: PROTOTYPE_VIEW_MESSAGE, view: view.view, state: view.state, choices: view.choices }, "*");
  };
  useEffect(() => { sendView(); }, [viewKey, mode, src]);
  const loadedSrc = useRef<string | null>(null);
  useLayoutEffect(() => { loadedSrc.current = null; }, [src]);
  const sendMode = () => frameRef.current?.contentWindow?.postMessage({ type: "yagni:prototype:comment-mode", enabled: commentMode }, "*");
  useEffect(() => {
    sendMode();
    const receive = (event: MessageEvent) => {
      if (!commentMode || mode !== "full" || loadedSrc.current !== src || event.source !== frameRef.current?.contentWindow) return;
      const point: unknown = event.data;
      if (!point || typeof point !== "object") return;
      const p = point as Record<string, unknown>;
      if (p.type !== "yagni:prototype:point") return;
      const keys = ["x", "y", "viewportWidth", "viewportHeight", "scrollX", "scrollY"] as const;
      if (keys.some(key => typeof p[key] !== "number" || !Number.isFinite(p[key]) || p[key] < 0 || p[key] > 1_000_000)) return;
      const { x, y, viewportWidth, viewportHeight, scrollX, scrollY } = p as unknown as PrototypePoint;
      if (!viewportWidth || !viewportHeight || viewportWidth > 10000 || viewportHeight > 10000 || x > viewportWidth || y > viewportHeight || typeof p.targetHint !== "string" || p.targetHint.length > 300) return;
      onPoint?.({ x, y, viewportWidth, viewportHeight, scrollX, scrollY, targetHint: p.targetHint });
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [src, mode, commentMode, onPoint]);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0.3);

  useLayoutEffect(() => {
    if (mode !== "card") return;
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / CARD_DESIGN_WIDTH);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [mode]);

  if (mode === "card") {
    return (
      <div
        ref={wrapRef}
        data-testid="prototype-frame-card"
        className="relative w-full overflow-hidden pointer-events-none select-none"
        style={{ aspectRatio: String(1 / CARD_ASPECT) }}
      >
        <iframe
          ref={cardFrameRef}
          onLoad={sendView}
          sandbox="allow-scripts"
          src={src}
          title={title}
          tabIndex={-1}
          className="absolute top-0 left-0 border-0 bg-[#ffffff]"
          style={{
            width: CARD_DESIGN_WIDTH,
            height: CARD_DESIGN_WIDTH * CARD_ASPECT,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        />
      </div>
    );
  }

  return (
    <iframe
      key={src}
      ref={frameRef}
      onLoad={() => { loadedSrc.current = src; sendMode(); sendView(); }}
      sandbox="allow-scripts"
      src={src}
      title={title}
      data-testid="prototype-frame-full"
      className="block h-full min-h-0 flex-1 border-0 bg-[var(--brand-white)]"
      style={
        width !== undefined
          ? { width, maxWidth: "100%", marginLeft: "auto", marginRight: "auto" }
          : { width: "100%" }
      }
    />
  );
}
